/**
 * song-edit-engine.js — the edit model for /remove-word-from-song
 *
 * THE ONE RULE: the instrumental is the clock. It is never cut, never moved,
 * never re-timed. Everything happens inside the vocal stem. That is why the
 * beat cannot break — not because we are careful, but because the only track
 * carrying the beat is read-only.
 *
 * Removing a syllable leaves a hole. Three ways to close it, best first —
 * order established by measurement on the reference file, not by taste:
 *
 *   shift    Cut the removed slot; the slider pulls the rest of the PHRASE in
 *            (0 = the freed time stays a pause). The phrase ends early and
 *            the rest after it absorbs that: from the rest on, the song is
 *            where it was — only the phrase moves, never the next line.
 *   stretch  Hold the neighbouring syllable across the hole. Always possible,
 *            but the note is now two slots long and reads as dragging — the
 *            user's own word for it was "подвисает".
 *   gap      Just mute it. Honest, audible, and the fallback of last resort.
 *
 * The tool must SAY which one it used and why; silently picking the worse one
 * is how "it sounds off and I don't know why" happens.
 *
 * planEdit() is pure so it can be unit-tested in Node; everything touching
 * WebAudio lives in createSession().
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    else root.SongEditEngine = api;
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    var GUARD_S = 0.20;      // stem-domain margin around the edit
    var XFADE_S = 0.012;     // seam feather inside the vocal
    var HYBRID_XF_S = 0.040; // crossfade back into untouched source audio

    /* ------------------------------------------------------------------ */
    /*  Pure planning                                                      */
    /* ------------------------------------------------------------------ */

    /**
     * Where the phrase that carries on after syllable `j` comes to a REST: the
     * first gap of PHRASE_REST_S or more between two syllables. Syllables end
     * where the voice ends (song-syllables.js), so that gap is the silence
     * itself — the place a pulled-in phrase hands the song back.
     *
     * Until 29.09.2026 this was a scan of the contour for a silence at least
     * as long as the WHOLE removed slot, and when a song had none (a held
     * note of 3 s, breaths of 0.5 s) the answer was «the file ends» — for
     * every such pick, anywhere in the song: 300 of 638 picks in two days,
     * 296 of them in the first 80 % of the song, each showing «Загружен
     * отрывок, а не вся песня». The pull-in then ran to the end of the voice,
     * and the export moved every later line off the beat (4 downloads with a
     * pull of ~2 s in 30 days). A rest of any length absorbs any pull: the
     * phrase ends earlier and the rest after it grows by as much.
     *
     * `fileEnds` is only the honest case: the voice is still sounding when
     * the audio stops — the file was cut mid-phrase.
     *
     * @returns {{atS:number, restS:number, fileEnds:boolean}} `atS` the phrase
     *          end, `restS` the silence after it (Infinity when the file ends)
     */
    function phraseRestAfter(syls, j, songEndS) {
        for (var k = j + 1; k + 1 < syls.length; k++) {
            var gap = syls[k + 1].start - syls[k].end;
            if (gap >= PHRASE_REST_S) return { atS: syls[k].end, restS: gap, fileEnds: false };
        }
        var last = syls[syls.length - 1];
        var tail = songEndS - last.end;
        return tail >= PHRASE_REST_S
            ? { atS: last.end, restS: tail, fileEnds: false }
            : { atS: songEndS, restS: Infinity, fileEnds: true };
    }

    function midS(s) { return (s.start + s.end) / 2; }

    /**
     * The run of syllables a mouse drag selects: `anchor` is the syllable under
     * the press (a press is a click until it moves), and any other syllable
     * joins once the pointer has crossed its MIDDLE — the moving edge snaps to
     * the nearest syllable boundary, the way a snapped selection works in any
     * audio editor.
     *
     * Until 29.09.2026 the syllable under the pointer joined at once (comment
     * #1780, «выделяет несколько секторов»). People click where a word
     * STARTS — on the line between two syllables — and a hand that drifts a
     * few pixels while clicking crossed it: on the real page at the opening
     * zoom an 8-px drift selected two syllables in 42 of 88 clicks, and in
     * prod 22 of 66 multi-syllable drags were at once taken back to one
     * syllable inside them. A drag that stops 4 px past the end of a word took
     * the next syllable in 18 of 33 tries.
     *
     * @returns {{from:number, to:number}}
     */
    function dragRange(syls, anchor, pressS, atS) {
        var from = anchor, to = anchor;
        if (atS >= pressS) { while (to + 1 < syls.length && atS >= midS(syls[to + 1])) to++; }
        else { while (from > 0 && atS <= midS(syls[from - 1])) from--; }
        return { from: from, to: to };
    }

    /** the syllable containing a time, or -1 */
    function syllableAt(syls, tS) {
        for (var i = 0; i < syls.length; i++)
            if (tS >= syls[i].start && tS < syls[i].end) return i;
        return -1;
    }

    /**
     * Same, but a click that lands between syllables snaps to the closest one
     * instead of doing nothing. Gaps between phrases are a large share of any
     * vocal track, and a dead click reads as a broken tool — caught by the
     * browser test, which clicked 20 ms into a gap and got silence.
     */
    function nearestSyllable(syls, tS, maxDistS) {
        var hit = syllableAt(syls, tS);
        if (hit >= 0) return hit;
        var best = -1, bestD = Infinity;
        for (var i = 0; i < syls.length; i++) {
            var d = tS < syls[i].start ? syls[i].start - tS
                  : tS > syls[i].end ? tS - syls[i].end : 0;
            if (d < bestD) { bestD = d; best = i; }
        }
        return (maxDistS && bestD > maxDistS) ? -1 : best;
    }

    /* A PHRASE END is a removal followed by a rest. Syllables end where their
     * voice ends (song-syllables.js, 27.09.2026), so the gap to the next onset
     * is a real silence, and at SPLIT_REST_S of it the removed words were the
     * end of a line: «pull the rest of the phrase in» has nothing to pull (the
     * next line starts after the break, on its own beat), and «hold the
     * neighbour» would hold a vowel over the break. Before the syllable end was
     * real, both were computed against the next ONSET: the last word of a song
     * «slotted» 5.2 s and a phrase-end word 26 s (prod, #1768) — stretch then
     * held a 100 ms vowel ×51 («какой-то скрип»), and a removal cut the vocal
     * for the whole break. */
    var PHRASE_REST_S = 0.25;
    // How long the neighbour may be held across the hole. The source is its
    // nucleus ±50 ms, so this is also a ceiling on the stretch ratio (×8): past
    // it Signalsmith holds a 100 ms vowel as a drone, not a note.
    var STRETCH_MAX_S = 0.8;
    // At a phrase end the cut runs on through the removed word's own decay
    // (reverb in the stem) until the voice is this far below the song's sung
    // level — never into the next line and never more than DECAY_MAX_S.
    var DECAY_BELOW_DB = 42, DECAY_MAX_S = 1.5, DECAY_HOLD_S = 0.12;

    function median(a) { var s = a.slice().sort(function (x, y) { return x - y; }); return s.length ? s[s.length >> 1] : 0; }

    /** where the removed word's decay has died away (see DECAY_BELOW_DB) */
    function decayEnd(contour, syls, fromS, limitS) {
        var cap = Math.min(limitS, fromS + DECAY_MAX_S);
        if (!contour || !contour.t || !contour.t.length) return Math.min(cap, fromS);
        var floor = median(syls.map(function (s) { return s.levelDb; })) - DECAY_BELOW_DB;
        var t = contour.t, db = contour.db, quietFrom = -1;
        for (var i = 0; i < t.length && t[i] <= cap; i++) {
            if (t[i] < fromS) continue;
            if (db[i] < floor) {
                if (quietFrom < 0) quietFrom = t[i];
                if (t[i] - quietFrom >= DECAY_HOLD_S) return Math.min(cap, quietFrom + 0.02);
            } else quietFrom = -1;
        }
        return cap;
    }

    /**
     * @param {object} o
     * @param {Array}  o.syllables   from SongSyllables.analyzeVocal
     * @param {object} o.contour     ditto (needed to find the phrase end)
     * @param {number} o.removeIndex first syllable to delete
     * @param {number} [o.removeTo]  last syllable to delete (a word or a phrase;
     *                               defaults to removeIndex)
     * @param {number} o.slotS       grid slot, for reporting only
     * @param {number} [o.durationS] length of the song (a phrase-end cut stops there)
     * @returns {object} plan
     */
    function planEdit(o) {
        var syls = o.syllables, i = o.removeIndex;
        if (!syls || i < 0 || i >= syls.length) throw new Error('bad removeIndex');
        var j = (o.removeTo != null && o.removeTo >= i) ? Math.min(o.removeTo, syls.length - 1) : i;
        var first = syls[i], last = syls[j];
        var removed = { start: first.start, end: last.end };
        var next = syls[j + 1];
        var prev = i > 0 ? syls[i - 1] : null;
        var restAfter = next ? next.start - last.end : Infinity;
        var phraseEnd = restAfter >= PHRASE_REST_S;
        var songEnd = o.durationS || (o.contour && o.contour.t && o.contour.t.length ? o.contour.t[o.contour.t.length - 1] : last.end);
        // Where the cut ends. Inside a phrase: at the next onset — the removed
        // SLOT, as always. At a phrase end: after the removed word's own decay.
        var cutTo = !phraseEnd ? next.start
            : decayEnd(o.contour, syls, last.end, next ? next.start - 0.03 : songEnd);
        // The shift distance comes from the AUDIO (onset to onset), not from
        // the grid: measured on the reference file, syllable onsets sit ~55 ms
        // after the drum-derived phase, so a grid-derived shift would be off
        // by that much every time. The grid only tells the user "that is one
        // eighth" — it never computes.
        var shiftS = cutTo - removed.start;
        var slots = o.slotS ? shiftS / o.slotS : null;

        // Inside a phrase the rest of it can always be pulled in: the rest
        // after it absorbs the pull (phraseRestAfter). At a phrase end there is
        // nothing to pull.
        var rest = next ? phraseRestAfter(syls, j, songEnd) : null;
        var canShift = !!next && !phraseEnd;
        var shiftReason = !next ? 'last_syllable'
            : phraseEnd ? 'phrase_end'
            : rest.fileEnds ? 'file_ends'      // the voice runs on past the end of the audio
            : 'clean';

        var stretchSource = next || prev;
        var holdS = (next && !phraseEnd) ? next.start - removed.start : shiftS;
        var canStretch = !!stretchSource && !phraseEnd && holdS <= STRETCH_MAX_S;
        var stretchReason = phraseEnd ? (next ? 'phrase_end' : 'last_syllable')
            : holdS > STRETCH_MAX_S ? 'too_long' : 'drags';

        // Tail source: the vowel of the PRECEDING syllable. Muting a syllable
        // outright does not sound like a rest, it sounds like the sound was
        // switched off — measured on the reference file, the mix drops 11.4 dB
        // in that window because the voice carries most of its energy. Letting
        // the previous note decay into the gap is what makes it read as a rest.
        var tailSrc = prev || removed;
        // Границы для тонкой настройки. Участок правки — это ВЕСЬ отрезок от
        // начала предыдущего слога до начала следующего: внутри него живут и
        // сам слог (который можно сжать, освобождая паузу), и его окончание,
        // и звон комнаты. Одним куском это собирать проще и безопаснее, чем
        // тремя клипами встык — стыки нас уже трижды подвели.
        // Конец участка — там же, где кончается вырез (cutTo): внутри фразы это
        // начало следующего слога, в конце фразы — затухание самого слова.
        // Участок нужен, только когда голос ПЕРЕТЕКАЕТ в удаляемое (легато):
        // там вырез обрывал бы звучащую ноту. Если перед словом уже пауза,
        // предыдущее слово допето само — трогать его растяжкой незачем, вне
        // выреза выход остаётся исходником байт в байт.
        var legatoIn = !!prev && (removed.start - prev.end) < 0.03;
        var fine = legatoIn ? {
            fromS: prev.start,
            breakS: prev.onsetEnd,          // граница согласный -> гласная
            prevEndS: prev.end,
            toS: cutTo,
            windowS: cutTo - prev.start
        } : null;

        var strategies = [
            {
                id: 'shift',
                available: canShift,
                reason: shiftReason,
                params: { shiftS: shiftS, fromS: removed.start,
                          untilS: rest ? rest.atS : cutTo, restS: rest ? rest.restS : 0 }
            },
            {
                id: 'stretch',
                available: canStretch,
                reason: stretchReason,
                params: {
                    fromS: removed.start,
                    toS: removed.start + holdS,
                    holdS: holdS,
                    sourceStart: stretchSource ? stretchSource.nucleus - 0.05 : 0,
                    sourceEnd: stretchSource ? Math.min(stretchSource.end, stretchSource.nucleus + 0.05) : 0
                }
            },
            {
                id: 'gap',
                available: true,
                // at a phrase end this is the natural choice, not the last resort
                reason: phraseEnd ? 'phrase_end' : 'audible',
                params: { fromS: removed.start, toS: cutTo }
            },
            // Replace the take, don't erase it: paste the SAME word from a spot
            // where it is sung right (suggestion stanislav.shevaldin 12.08 —
            // Suno sings one chorus clean and one with a defect). Deliberately
            // LAST in this array: `chosen = first available` must never land
            // here, because a replace cannot be recommended before the user has
            // pointed at a donor. UI order comes from the DOM, not from here.
            {
                id: 'replace',
                available: syls.length > 1,
                reason: 'needs_donor',
                params: { fromS: removed.start, toS: removed.end }
            }
        ];

        var chosen = strategies.find(function (s) { return s.available && s.id !== 'replace'; });
        strategies.forEach(function (s) { s.recommended = (s === chosen); });

        return {
            removed: { start: removed.start, end: removed.end, durationS: removed.end - removed.start,
                       from: i, to: j, count: j - i + 1 },
            phraseEnd: phraseEnd,
            cutTo: cutTo,
            shiftS: shiftS,
            slots: slots,
            rest: rest,
            strategies: strategies,
            recommended: chosen ? chosen.id : 'gap',
            fine: fine,
            legatoIn: legatoIn,
            tail: {
                sourceStart: Math.max(0, tailSrc.nucleus - 0.05),
                sourceEnd: Math.min(tailSrc.end, tailSrc.nucleus + 0.05),
                maxS: Math.max(0.05, shiftS)
            }
        };
    }

    /* ------------------------------------------------------------------ */
    /*  Clip list — one representation for preview AND render              */
    /* ------------------------------------------------------------------ */

    /**
     * A clip = a slice of a source buffer placed on the output timeline.
     * Preview schedules them; the offline render draws the same list. One
     * representation means what you hear while dragging is what you export.
     */
    function buildClips(plan, strategyId, dur, stretched, shiftOverrideS, tail, rep) {
        var s = plan.strategies.find(function (x) { return x.id === strategyId; });
        if (!s) throw new Error('unknown strategy ' + strategyId);
        var p = s.params, clips = [];
        // The computed shift is a principled DEFAULT, not an answer. Measured on
        // the reference file: the phrase had a rest before it, and once one
        // syllable is gone that rest reads as a hesitation — the ear wants to
        // eat into it. Only listening can find the amount, so the amount is a
        // live parameter and every consumer (preview AND export) reads it here.
        var shiftS = (shiftOverrideS != null && isFinite(shiftOverrideS))
            ? Math.max(0, shiftOverrideS) : plan.shiftS;
        var push = function (srcOffset, dstTime, duration, src, fade) {
            if (!(duration > 0.0005)) return;
            var c = { src: src || 'vocal', srcOffset: srcOffset, dstTime: dstTime, duration: duration };
            // a seam between two different stretches of the vocal is feathered
            // (clipFade below) — the same ramps in the preview and in the render
            if (fade && fade.inS > 0) c.fadeInS = Math.min(fade.inS, duration / 2);
            if (fade && fade.outS > 0) c.fadeOutS = Math.min(fade.outS, duration / 2);
            clips.push(c);
        };
        // The decaying note-ending, laid over the freed time. `available` is how
        // much silence there actually is — a tail longer than the gap would run
        // into the next syllable.
        // Готовый участок правки кладётся ОДНИМ клипом: он уже содержит сжатый
        // слог, его окончание и звон комнаты, собранные без стыков внутри.
        var addPatch = function (list, t) {
            if (!t || !(t.seconds > 0.005)) return;
            list.push({ src: 'tail', srcOffset: 0, dstTime: t.fromS, duration: t.seconds });
        };

        if (strategyId === 'shift') {
            // TWO independent quantities, and conflating them was the hs-4 bug
            // (12.08.2026: «в лупе слог пропадает, в скачанном — нет»):
            //   · the CUT is always the whole removed slot — the syllable goes
            //     no matter where the slider sits;
            //   · the SLIDER is only how far the remainder is pulled in
            //     (0 = the freed time stays as a pause).
            // The old formula cut `shiftS` seconds, so at the default slider (0,
            // floored to 5 ms) the export kept the syllable — measured on the
            // reference file: corr(export, vocal stem) = 0.858 at +5 ms lag
            // in the removed window. Pulling PAST the slot still eats the rest
            // in front of the phrase: the cut start walks back, its end never moves.
            var cutEnd = p.fromS + plan.shiftS;
            var cutStart = Math.min(p.fromS, Math.max(0, cutEnd - shiftS));
            // если есть собранный участок — вокал обрывается на его начале
            var patchFrom = (tail && tail.fromS != null) ? Math.min(tail.fromS, cutStart) : cutStart;
            push(0, 0, patchFrom);
            // Only the PHRASE moves. Until 29.09.2026 everything after its end
            // was laid `shiftS` earlier too: the preview played every later
            // line off the beat, and where no «room» was found (the end of the
            // phrase was then the end of the voice) so did the export. Now the
            // pulled phrase runs on into its own rest for as long as it was
            // pulled (never into the next line) and hands back to the song IN
            // PLACE inside that rest, feathered: the rest grows by the pull.
            var handBack = Math.max(cutEnd, p.untilS);
            var runOn = Math.min(shiftS, Math.max(0, (p.restS != null ? p.restS : 0) - XFADE_S));
            // a file that stops mid-phrase: the phrase runs to the end of the audio
            var srcEnd = Math.min(dur, handBack + runOn + XFADE_S);
            push(cutEnd, cutEnd - shiftS, srcEnd - cutEnd, 'vocal', { outS: XFADE_S });
            push(handBack, handBack, Math.max(0, dur - handBack), 'vocal', { inS: XFADE_S });
            addPatch(clips, tail);
            return { clips: clips, editFrom: patchFrom, editTo: handBack + XFADE_S };
        }
        if (strategyId === 'gap') {
            var gFrom = (tail && tail.fromS != null) ? Math.min(tail.fromS, p.fromS) : p.fromS;
            push(0, 0, gFrom);
            push(p.toS, p.toS, Math.max(0, dur - p.toS));
            addPatch(clips, tail);
            return { clips: clips, editFrom: gFrom, editTo: p.toS };
        }
        if (strategyId === 'replace') {
            // The donor take, pre-baked on the page: stretched to the target
            // span, level-matched, crossfades to the surrounding vocal already
            // INSIDE the buffer (same "one seamless piece" philosophy as the
            // fine patch — butt joints are where three audible seams came from).
            // No donor picked yet = play the vocal untouched; the edit window
            // still points at the target so A/B and the loop stay meaningful.
            if (!rep || !(rep.seconds > 0.005)) {
                push(0, 0, dur);
                return { clips: clips, editFrom: p.fromS, editTo: p.toS };
            }
            var repEnd = rep.fromS + rep.seconds;
            push(0, 0, rep.fromS);
            clips.push({ src: 'replace', srcOffset: 0, dstTime: rep.fromS, duration: rep.seconds });
            push(repEnd, repEnd, Math.max(0, dur - repEnd));
            return { clips: clips, editFrom: rep.fromS, editTo: repEnd };
        }
        // stretch: the middle comes from a pre-rendered buffer
        push(0, 0, p.fromS);
        if (stretched)
            clips.push({ src: 'stretch', srcOffset: 0, dstTime: p.fromS, duration: p.toS - p.fromS });
        push(p.toS, p.toS, Math.max(0, dur - p.toS));
        return { clips: clips, editFrom: p.fromS, editTo: p.toS };
    }

    /* ------------------------------------------------------------------ */
    /*  Browser session                                                    */
    /* ------------------------------------------------------------------ */

    /**
     * The feathered edges of a clip (fadeInS / fadeOutS, set by buildClips) as
     * a GainNode on `ac`'s clock, where timeline time T plays at
     * `at0 + (T - fromS)`. Linear: the preview and the render get the very
     * same ramps, so what is heard across a seam is what is exported. null when
     * the clip has no feathered edge.
     */
    function clipFade(ac, c, fromS, at0) {
        var fi = c.fadeInS > 0 ? c.fadeInS : 0, fo = c.fadeOutS > 0 ? c.fadeOutS : 0;
        if (!fi && !fo) return null;
        var endT = c.dstTime + c.duration;
        var level = function (T) {
            var v = 1;
            if (fi) v = Math.min(v, (T - c.dstTime) / fi);
            if (fo) v = Math.min(v, (endT - T) / fo);
            return Math.max(0, Math.min(1, v));
        };
        var at = function (T) { return at0 + (T - fromS); };
        var g = ac.createGain();
        var T0 = Math.max(c.dstTime, fromS);
        g.gain.setValueAtTime(level(T0), at(T0));
        if (fi && T0 < c.dstTime + fi) g.gain.linearRampToValueAtTime(1, at(c.dstTime + fi));
        if (fo) {
            if (T0 < endT - fo) g.gain.setValueAtTime(1, at(endT - fo));
            g.gain.linearRampToValueAtTime(0, at(endT));
        }
        return g;
    }

    function createSession(cfg) {
        var ctx = cfg.ctx;
        var buffers = { vocal: cfg.vocal, instrumental: cfg.instrumental, stretch: null, tail: null, replace: null };
        var patchAt = null, replaceAt = null;
        var original = cfg.original;
        var dur = original.duration;
        var plan = null, strategy = 'shift', layout = null, shiftOverride = null;
        var nodes = [], master = null, gVoc = null, gIns = null;
        var solo = 'mix', abOriginal = false;
        var startedAt = 0, startOffset = 0, playing = false;
        var loopRegion = null, loopTimer = null;
        var onEnded = cfg.onEnded || function () {};
        var onTick = cfg.onTick || function () {};

        function rebuild() {
            layout = plan ? buildClips(plan, strategy, dur, buffers.stretch, shiftOverride,
                                       (buffers.tail && patchAt != null)
                                           ? { fromS: patchAt, seconds: buffers.tail.duration }
                                           : null,
                                       (buffers.replace && replaceAt != null)
                                           ? { fromS: replaceAt, seconds: buffers.replace.duration }
                                           : null) : null;
        }

        function stopNodes() {
            nodes.forEach(function (n) { try { n.stop(); } catch (_) {} try { n.disconnect(); } catch (_) {} });
            nodes = [];
            if (loopTimer) { clearTimeout(loopTimer); loopTimer = null; }
        }

        function schedule(fromS) {
            stopNodes();
            master = ctx.createGain(); master.connect(ctx.destination);
            gVoc = ctx.createGain(); gIns = ctx.createGain();
            gVoc.connect(master); gIns.connect(master);
            gVoc.gain.value = (solo === 'instrumental') ? 0 : 1;
            gIns.gain.value = (solo === 'vocal') ? 0 : 1;

            var t0 = ctx.currentTime + 0.03;
            var play = function (buf, srcOffset, dstTime, duration, out, clip) {
                var end = dstTime + duration;
                if (end <= fromS) return;
                var skip = Math.max(0, fromS - dstTime);
                var when = t0 + Math.max(0, dstTime - fromS);
                var src = ctx.createBufferSource();
                src.buffer = buf;
                var fade = clip ? clipFade(ctx, clip, fromS, t0) : null;
                if (fade) { src.connect(fade); fade.connect(out); nodes.push(fade); }
                else src.connect(out);
                // Смещение в буфере не может быть отрицательным: WebKit на этом
                // бросает `RangeError: offset value should be positive` и рвёт
                // ВЕСЬ запуск воспроизведения (Sentry GN, Mobile Safari).
                //
                // Отрицательное смещение означает «начать раньше начала записи»,
                // а там звука нет. Правильный ответ — не обрезать до нуля молча
                // (это сдвинуло бы клип во времени и рассинхронизировало бы его
                // с инструменталом), а НАЧАТЬ ПОЗЖЕ ровно на недостающее и на
                // столько же укоротить: клип остаётся на своём месте на шкале.
                var off = srcOffset + skip;
                var len = duration - skip;
                var at = when;
                if (off < 0) {
                    at = when - off;   // off отрицателен → старт сдвигается вперёд
                    len = len + off;
                    // Число, которое привело сюда, должно быть НАЗВАНО: иначе в
                    // следующий раз опять придётся гадать, чья арифметика ушла в
                    // минус. Здесь только гасим последствие.
                    try {
                        window.__aeDiagnostic && window.__aeDiagnostic({
                            issue: 'song_edit_negative_offset',
                            d: 'srcOffset=' + srcOffset + ' skip=' + skip + ' dur=' + duration
                        });
                    } catch (_) {}
                    off = 0;
                }
                if (!(len > 0)) return;
                src.start(at, off, len);
                nodes.push(src);
            };

            if (abOriginal) {
                play(original, fromS, fromS, dur - fromS, master);
            } else {
                play(buffers.instrumental, fromS, fromS, dur - fromS, gIns);
                (layout ? layout.clips : [{ src: 'vocal', srcOffset: 0, dstTime: 0, duration: dur }])
                    .forEach(function (c) {
                        var b = buffers[c.src];
                        if (b) play(b, c.srcOffset, c.dstTime, c.duration, gVoc, c);
                    });
            }
            startedAt = t0; startOffset = fromS; playing = true;

            if (loopRegion) {
                var ms = (loopRegion.end - fromS) * 1000;
                loopTimer = setTimeout(function () { schedule(loopRegion.start); }, Math.max(30, ms));
            }
        }

        function tick() {
            if (playing) { onTick(position()); requestAnimationFrame(tick); }
        }
        function position() {
            // ⚠️ Запуск планируется с запасом (`t0 = currentTime + 0.03`), поэтому
            // первые ~30 мс после `play()` разность `currentTime - startedAt`
            // ОТРИЦАТЕЛЬНА. Позиции «до начала трека» не существует, а вот
            // последствие было: `setShiftS` в этот момент звал `schedule(-0.03)`,
            // и оттуда отрицательное смещение уходило в
            // `AudioBufferSourceNode.start()` — WebKit бросал
            // `RangeError: offset value should be positive` и рвал весь запуск
            // (Sentry GN). Человек всего лишь двинул ползунок сразу после
            // нажатия «играть».
            var t = playing ? startOffset + (ctx.currentTime - startedAt) : startOffset;
            return t > 0 ? t : 0;
        }

        /** samples the render rewrites: the edit window, its guard and the crossfade back */
        function editWindow() {
            if (!layout) return null;
            var sr = original.sampleRate, n = original.length, xf = Math.round(HYBRID_XF_S * sr);
            var a = Math.max(0, Math.floor((layout.editFrom - GUARD_S) * sr));
            var b = Math.min(n, Math.ceil((layout.editTo + GUARD_S) * sr));
            return { from: Math.max(0, a - xf), to: Math.min(n, b + xf), a: a, b: b, xf: xf,
                     fromS: Math.max(0, a - xf) / sr, toS: Math.min(n, b + xf) / sr };
        }

        /** offline render, then restore the source outside the edited span */
        function render() { return renderWith(true, original); }
        /**
         * The VOCAL alone through the same clip list, restored to the vocal stem
         * outside the edit window — what an applied edit leaves in the vocal
         * lane. With render() it is the whole of «apply»: the mix goes back into
         * `original`, this into the vocal stem, and the next edit starts from
         * both. One clip list, so the lane shows exactly what is exported.
         */
        function renderVocal() { return renderWith(false, buffers.vocal); }

        function renderWith(withInst, base) {
            if (!layout) return Promise.resolve(base);
            var sr = base.sampleRate, n = base.length;
            var off = new OfflineAudioContext(base.numberOfChannels, n, sr);
            if (withInst) {
                var ins = off.createBufferSource(); ins.buffer = buffers.instrumental;
                ins.connect(off.destination); ins.start(0);
            }
            layout.clips.forEach(function (c) {
                var b = buffers[c.src]; if (!b) return;
                var s = off.createBufferSource(); s.buffer = b;
                var g = clipFade(off, c, 0, 0);
                if (!g) { g = off.createGain(); g.gain.value = 1; }
                s.connect(g); g.connect(off.destination);
                // Тот же запрет на отрицательное смещение, что и в
                // воспроизведении: экспорт обязан вести себя одинаково с тем,
                // что человек слышал, а не падать на своём месте.
                var eoff = Math.max(0, c.srcOffset);
                var eat = c.dstTime + (eoff - c.srcOffset);
                var d = Math.min(c.duration - (eoff - c.srcOffset), b.duration - eoff);
                if (d > 0) s.start(eat, eoff, d);
            });
            return off.startRendering().then(function (mixed) {
                var out = off2buffer(mixed, sr);
                var a = Math.max(0, Math.floor((layout.editFrom - GUARD_S) * sr));
                var b = Math.min(n, Math.ceil((layout.editTo + GUARD_S) * sr));
                var xf = Math.round(HYBRID_XF_S * sr);
                // LINEAR, not equal-power. Across the guard the render IS the
                // source (instrumental + the untouched vocal = the mix), so the
                // two sides of this seam are the same signal, and an equal-power
                // law sums correlated signals to +3 dB at its midpoint: until
                // 27.09.2026 every export swelled the WHOLE mix by up to 3 dB for
                // 40 ms at both edges of the edit (measured: the vocal read 42 dB
                // above the source there). A linear crossfade of identical
                // content is the identity. Same reasoning as spliceStems below.
                for (var ch = 0; ch < out.numberOfChannels; ch++) {
                    var o = out.getChannelData(ch), src = base.getChannelData(Math.min(ch, base.numberOfChannels - 1));
                    for (var i = 0; i < a - xf; i++) o[i] = src[i];
                    for (var j = b + xf; j < n; j++) o[j] = src[j];
                    for (var k = 0; k < xf; k++) {
                        var r = k / xf;
                        var pi = a - xf + k;
                        if (pi >= 0 && pi < n) o[pi] = src[pi] * (1 - r) + o[pi] * r;
                        var qi = b + k;
                        if (qi >= 0 && qi < n) o[qi] = o[qi] * (1 - r) + src[qi] * r;
                    }
                }
                return out;
            });
        }
        function off2buffer(b) { return b; }

        return {
            setPlan: function (p) {
                // A patch is assembled FOR one selection's window; a new plan
                // object means a new selection, and the old patch played at its
                // old position would double the voice there until the fresh one
                // arrives. Same object (strategy toggle) keeps the valid patch.
                // The donor take is tied to a selection the same way.
                if (p !== plan) {
                    buffers.tail = null; patchAt = null;
                    buffers.replace = null; replaceAt = null;
                }
                plan = p; shiftOverride = null; rebuild();
            },
            setStrategy: function (id) { strategy = id; rebuild(); },
            /**
             * Live shift adjustment. Reschedules in place: the instrumental keeps
             * playing from the same buffer, only the vocal clips move, so the
             * change is heard on the next loop pass without a re-render.
             */
            setShiftS: function (v) {
                shiftOverride = v;
                rebuild();
                if (playing) schedule(position());
            },
            getShiftS: function () {
                return shiftOverride != null ? shiftOverride : (plan ? plan.shiftS : 0);
            },
            setStretchBuffer: function (b) { buffers.stretch = b; rebuild(); },
            /** the pre-baked donor take (see buildClips 'replace') placed at atS */
            setReplaceBuffer: function (buf, atS) {
                buffers.replace = buf; replaceAt = (buf ? atS : null);
                rebuild();
                if (playing) schedule(position());
            },
            /**
             * The whole edit window, pre-assembled: squeezed syllable + its
             * release + room tail, in one seamless buffer placed at `atS`.
             * Rebuilt live while a slider moves; nothing is joined butt-to-butt
             * inside the timeline, which is how three audible seams got in.
             */
            setPatch: function (buf, atS) {
                buffers.tail = buf; patchAt = (buf ? atS : null);
                rebuild();
                if (playing) schedule(position());
            },
            getLayout: function () { return layout; },
            play: function (fromS, loop) {
                loopRegion = loop || null;
                schedule(fromS || 0);
                requestAnimationFrame(tick);
            },
            stop: function () { stopNodes(); playing = false; onEnded(); },
            isPlaying: function () { return playing; },
            position: position,
            setSolo: function (s) {
                solo = s;
                if (gVoc) gVoc.gain.value = (s === 'instrumental') ? 0 : 1;
                if (gIns) gIns.gain.value = (s === 'vocal') ? 0 : 1;
            },
            setAB: function (useOriginal) {
                abOriginal = !!useOriginal;
                if (playing) schedule(position());
            },
            render: render,
            renderVocal: renderVocal,
            editWindow: editWindow
        };
    }

    /* ------------------------------------------------------------------ */
    /*  Precision window — a better separation of ONE stretch, spliced in   */
    /* ------------------------------------------------------------------ */

    /**
     * Write `src` into `dst` starting at sample `at`, dissolving the edges:
     * the first `xfadeIn` and last `xfadeOut` samples cross-fade LINEARLY
     * from what the buffer held to the new content. Linear, not equal-power,
     * on purpose: both sides are the same voice from the same recording
     * (one separation replaced by a better one), i.e. strongly correlated —
     * an equal-power law sums correlated signals to +3 dB at the midpoint,
     * a linear one leaves identical content untouched. Fewer source channels
     * than destination channels → the last one repeats. Nothing is
     * allocated: the edit lands in the very buffer the session and the
     * waveform already hold, which is the point — every consumer sees the
     * refined stem without being told.
     *
     * Why this exists (12.09.2026, #6943): CascadedNet leaves the removed
     * syllable audible in the "instrumental" (measured: −8…−10 dB against
     * the music in the window), Kim_Vocal_2 does not (−33…−35 dB) — but Kim
     * costs 8.5–14× the song length on a phone without WebGPU. The render
     * restores the original outside the edit window anyway, so only the
     * window needs the better model: one 5.7-second chunk, spliced here.
     *
     * @param {AudioBuffer|{numberOfChannels:number,length:number,getChannelData:function}} dst
     * @param {AudioBuffer|{numberOfChannels:number,length:number,getChannelData:function}} src
     * @param {number} at      destination sample offset
     * @param {{xfadeIn?:number, xfadeOut?:number}} [opts]  fade lengths in samples
     * @returns {number} samples written
     */
    function spliceStems(dst, src, at, opts) {
        opts = opts || {};
        at = Math.max(0, at | 0);
        var n = Math.min(src.length, dst.length - at);
        if (n <= 0) return 0;
        var xfIn = Math.max(0, Math.min(opts.xfadeIn | 0, n >> 1));
        var xfOut = Math.max(0, Math.min(opts.xfadeOut | 0, n >> 1));
        for (var c = 0; c < dst.numberOfChannels; c++) {
            var d = dst.getChannelData(c);
            var s = src.getChannelData(Math.min(c, src.numberOfChannels - 1));
            for (var i = 0; i < n; i++) {
                var w = 1;
                if (i < xfIn) w = i / xfIn;
                else if (i >= n - xfOut) w = (n - 1 - i) / xfOut;
                if (w >= 1) d[at + i] = s[i];
                else d[at + i] = d[at + i] * (1 - w) + s[i] * w;
            }
        }
        return n;
    }

    return {
        planEdit: planEdit,
        buildClips: buildClips,
        phraseRestAfter: phraseRestAfter,
        dragRange: dragRange,
        syllableAt: syllableAt,
        nearestSyllable: nearestSyllable,
        createSession: createSession,
        spliceStems: spliceStems,
        constants: { GUARD_S: GUARD_S, XFADE_S: XFADE_S, HYBRID_XF_S: HYBRID_XF_S,
                     PHRASE_REST_S: PHRASE_REST_S, STRETCH_MAX_S: STRETCH_MAX_S },
        version: '1.3.0'
    };
});
